"use client";

import { Box, Brain, Globe, Workflow } from "lucide-react";
import { useTranslations } from "next-intl";
import { SECTION_TITLE } from "@shared/constants/typography";

interface Feature {
	icon: React.ComponentType<{ className?: string }>;
	iconClassName: string;
	bgClassName: string;
	title: string;
	description: string;
}

export function BatchWhyUseSection() {
	const t = useTranslations("batchImageToPrompt.components.whyUse");

	const features: Feature[] = [
		{
			icon: Brain,
			iconClassName: "text-primary",
			bgClassName: "bg-primary/10",
			title: t("batchProcessing.title"),
			description: t("batchProcessing.description"),
		},
		{
			icon: Box,
			iconClassName: "text-secondary",
			bgClassName: "bg-secondary/15",
			title: t("multipleModels.title"),
			description: t("multipleModels.description"),
		},
		{
			icon: Globe,
			iconClassName: "text-accent",
			bgClassName: "bg-accent/15",
			title: t("multilingual.title"),
			description: t("multilingual.description"),
		},
		{
			icon: Workflow,
			iconClassName: "text-info",
			bgClassName: "bg-info/15",
			title: t("workflow.title"),
			description: t("workflow.description"),
		},
	];

	return (
		<section className="py-12 sm:py-16">
			<div className="container mx-auto px-4">
				<div className="text-center mb-10 sm:mb-12">
					<h2 className={`${SECTION_TITLE.className} mb-4`}>
						{t("title")}
					</h2>
				</div>

				<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-6 max-w-4xl mx-auto">
					{features.map((feature, index) => {
						const Icon = feature.icon;
						return (
							<div
								key={index}
								className="group relative rounded-2xl border border-border bg-card shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden"
							>
								<div className="relative p-6">
									<div className="mb-4">
										<div
											className={`w-12 h-12 rounded-xl ${feature.bgClassName} flex items-center justify-center group-hover:scale-110 transition-transform duration-300`}
										>
											<Icon
												className={`w-7 h-7 ${feature.iconClassName}`}
											/>
										</div>
									</div>

									<h3 className="text-lg font-semibold text-foreground mb-2">
										{feature.title}
									</h3>

									<p className="text-sm text-muted-foreground leading-relaxed">
										{feature.description}
									</p>

									<div className="absolute bottom-0 left-0 right-0 h-1 bg-primary/70 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
								</div>
							</div>
						);
					})}
				</div>
			</div>
		</section>
	);
}
